import Box from "@mui/material/Box";
import React, {useMemo} from "react";
import {useTheme} from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import {numberWithCommas} from "@/lib/helper-functions";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import moment from 'moment-jalaali';
import Typography from "@mui/material/Typography";
import {ResponsiveLine} from "@nivo/line";
import Image from "next/image";
import {useProductPriceChart} from "@/lib/api";
import {ChartData, ExtendedDataPoint} from "@/types/product";
import useMediaQuery from "@mui/material/useMediaQuery";

const seasonNames = ['بهار', 'تابستان', 'پاییز', 'زمستان'];


export default function PPriceChart({
                                        productName,
                                        productId,
                                    }: { productName: string; productId: number }) {
    const theme = useTheme();
    const {width} = useWindowDimensions();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const {data: chart} = useProductPriceChart(productId) as {
        data: ChartData[] | undefined;
        isLoading: boolean;
        error: any;
    };

    const dataPoints = useMemo(() => {
        if (!chart) return [];

        return chart.map((item, index, array) => {
            const date = moment(item.date, "jYYYY/jMM/jDD");
            const seasonIndex = Math.floor(date.jMonth() / 3);
            const season = `${seasonNames[seasonIndex]} ${date.jYear()}`;
            const isFirstOfSeason = index === 0 || Math.floor(moment(array[index - 1].date, "jYYYY/jMM/jDD").jMonth() / 3) !== seasonIndex;

            return {
                x: date.format('YYYY-MM-DD'),
                y: item.price,
                originalDate: item.date,
                season: isFirstOfSeason ? season : '',
            };
        });
    }, [chart]);

    const prices = dataPoints.map(d => d.y);
    const maxPrice = prices?.length ? Math.max(...prices) : 0;
    const minPrice = prices?.length ? Math.min(...prices) : 0;
    const padding = 0.1 * (maxPrice - minPrice);
    const yMin = Math.floor((minPrice - padding) / 1000) * 1000;
    const yMax = Math.ceil((maxPrice + padding) / 1000) * 1000;
    const tickStep = Math.round((yMax - yMin) / 7 / 1000) * 1000;
    const yTicks = Array.from({length: 8}, (_, i) => yMin + tickStep * i);
    const latestPrice = dataPoints?.length > 0 ? dataPoints[dataPoints?.length - 1].y : 0;

    const extendedDataPoints = useMemo(() => {
        if (!chart) return [];

        const parsed = chart.map(item => {
            const date = moment(item.date, "jYYYY/jMM/jDD");

            const seasonIndex = Math.floor(date.jMonth() / 3);
            const season = `${seasonNames[seasonIndex]} ${date.jYear()}`;

            return {
                y: item.price,
                dateObj: date,
                season,
            };
        });

        const grouped = parsed.reduce((acc: Record<string, typeof parsed>, item) => {
            if (!acc[item.season]) acc[item.season] = [];
            acc[item.season].push(item);
            return acc;
        }, {});

        const last5Seasons = Object.entries(grouped).slice(-5);
        const result: ExtendedDataPoint[] = [];

        last5Seasons.forEach(([season, items], seasonIndex) => {
            (items as typeof parsed).sort((a, b) => a.dateObj.unix() - b.dateObj.unix());
            const total = items.length;

            items.forEach((item, i) => {
                const offset = (i / total) * 0.8;
                result.push({
                    x: seasonIndex + offset,
                    y: item.y,
                    season: i === 0 ? season : undefined,
                    rawDate: item.dateObj.format('YYYY-MM-DD')
                });
            });
        });

        return result;
    }, [chart]);

    const emptyChartState = dataPoints.length === 0;
    const hasNoPriceHistory = dataPoints.length === 0;
    const hasNotEnoughPriceHistory = dataPoints.length < 2;

    if (hasNoPriceHistory || hasNotEnoughPriceHistory) {
        return (
            <Box
                height={400}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                width="100%"
            >
                <Image
                    alt={productName}
                    src="/images/icons/no-chart.png"
                    width={250}
                    height={250}
                    style={{ objectFit: "contain" }}
                />

                <Typography fontSize={18} mb={1} fontWeight={400}>
                    {emptyChartState
                        ? "اطلاعاتی برای نمایش وجود ندارد"
                        : "اطلاعات کافی برای رسم نمودار وجود ندارد"}
                </Typography>

                <Typography color="text.secondary">
                    {emptyChartState
                        ? "برای این کالا تاریخچه قیمت ثبت نشده است."
                        : "برای رسم نمودار قیمت، حداقل دو نقطه قیمتی لازم است."}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            height={400}
            margin="auto"
            width="100%"
            sx = {{
            '& text': {
                fontFamily: `${theme.typography.fontFamily} !important`,
                fontWeight: 400,
                fill: `${theme.palette.grey[600]} !important`
            }
        }}
        >

                <ResponsiveLine
                    enableArea
                    areaBaselineValue = {yMin}
                    curve = "monotoneX"
                    areaOpacity = {0.15}
                    markers = {[{
                        axis: 'y',
                        value: latestPrice,
                        lineStyle: {stroke: theme.palette.primary.main, strokeWidth: 1, strokeDasharray: 4},
                        legend: `${numberWithCommas(latestPrice)} ریال`,
                        legendPosition: 'top-right',
                        // @ts-ignore
                        legendOffsetX: 100,
                        textStyle: {
                            fontSize: 14,
                            fill: theme.palette.primary.main,
                        }
                    }]}
                    margin = {{top: 30, right: 30, bottom: isMobile ? 60 : 50, left: 60}}
                    axisLeft = {{
                        format: v => numberWithCommas(v),
                        tickValues: yTicks,
                        tickSize: 0,
                        legend: "",
                        legendPosition: "middle",
                        legendOffset: -60,
                        tickRotation: 0,
                        tickPadding: 50,
                    }}
                    axisBottom = {{
                        tickValues: extendedDataPoints.filter(d => d.season).map(d => d.x),
                        tickPadding: 12,
                        tickSize: 8,
                        tickRotation: width > theme.breakpoints.values.lg ? 0 : -35,
                        format: value => {
                            const label = extendedDataPoints.find(d => d.x === value)?.season;
                            return label || '';
                        },
                    }}
                    enableGridX = {false}
                    gridYValues = {yTicks}
                    colors = {[theme.palette.primary.main]}
                    data = {[{
                        id: 'قیمت',
                        data: extendedDataPoints.map(d => ({x: d.x, y: d.y}))
                    }]}
                    xScale = {{
                        type: 'linear',
                        min: 0,
                        max: Math.max(...extendedDataPoints.map(d => d.x)),
                    }}
                    yScale = {{type: 'linear', min: yMin, max: yMax}}
                    useMesh
                    enableSlices = {false}
                    pointLabelYOffset = {-12}
                    pointSize = {10}
                    pointColor = {theme.palette.primary.light}
                    pointBorderWidth = {2}
                    pointBorderColor = {{from: 'serieColor'}}
                    legends = {[]}
                    theme = {{
                        axis: {
                            ticks: {
                                text: {
                                    fontSize: 12,
                                    fill: theme.palette.grey[700],
                                },
                            },
                            legend: {
                                text: {
                                    fontSize: 14,
                                    fill: theme.palette.grey[800],
                                },
                            },
                        },
                        grid: {
                            line: {
                                stroke: theme.palette.grey[300],
                                strokeWidth: 1,
                            },
                        },
                    }}
                    tooltip = {({point}) => {
                        const raw = extendedDataPoints.find(p => p.x === point.data.x);
                        const jalaliDate = raw ? moment(raw.rawDate).locale('fa').format('jYYYY/jMM/jDD') : '';
                        const isNearRightEdge = point.index === dataPoints.length - 1;
                        const tooltipStyle = {
                            position: 'relative',
                            left: isNearRightEdge ? '-40px' : '0px',
                            width: 160,
                        };

                        return (
                            <Paper sx = {{
                                p: 1.2,
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 2,
                                background: theme.palette.background.paper
                            }}
                                // @ts-ignore
                                style = {tooltipStyle}
                            >
                                <Box mb = {0.5} display = 'flex' justifyContent = 'space-between' >
                                    <Typography fontSize = {13} color = 'text.secondary' >تاریخ:</Typography >
                                    <Typography fontSize = {13} color = 'text.primary' >{jalaliDate}</Typography >
                                </Box >

                                <Box display = 'flex' justifyContent = 'space-between' >
                                    <Typography fontSize = {13} color = 'text.secondary' >قیمت:</Typography >
                                    <Typography fontSize = {14} fontWeight = {500} color = 'text.primary' >
                                        {numberWithCommas(Number(point?.data.y))} ریال
                                    </Typography >
                                </Box >

                            </Paper >
                        );
                    }}
                />
        </Box >
    );
}
